import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { canWrite } from '../../utils/roles';
import InlineConsolidator from '../../components/InlineConsolidator';
import StockChip from '../../components/StockChip';
import ConfirmDialog from '../../components/ConfirmDialog';
import useEquipmentSearch, { EQ_DIMENSIONS, EQ_DEFAULT_DIMENSIONS } from './useEquipmentSearch';
import './EquipmentPlanningPage.css';

function IconPencil() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );
}
function IconTrash() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
      <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  );
}

// ── MOCK para simulación visual — quitar antes de producción ──
const MOCK_MODE = false;

let _id = 9000;
function zones(cats) {
  return cats.flatMap(({ zone, names }) =>
    names.map(name => ({ id: _id++, current_name: name, category: zone, sort_order: _id, links: [] }))
  );
}

const MOCK_EVENTS = [
  {
    id: 1, name: 'Boda García-Martínez', client: 'Familia García', place: 'Hotel Sofitel Carrasco',
    status: 'confirmado', start_time: '18:00', end_time: '01:00', event_date: null,
    checklist: { id: 1, items: zones([
      { zone: 'Audio', names: ['Sistema de audio principal', 'Micrófono inalámbrico solapa x2', 'Micrófono de mano x2', 'Monitor de escenario x2', 'Consola de mezcla 32ch', 'Mesa DJ con flightcase'] },
      { zone: 'Video', names: ['Pantalla LED 3x2m', 'Proyector 10.000 lm'] },
      { zone: 'Iluminación', names: ['Iluminación ambiental salón', 'Par LED x12', 'Cañón de confetti', 'Máquina de humo'] },
    ]) }
  },
  {
    id: 2, name: 'Congreso Médico CUMS', client: 'CUMS', place: 'Centro de Convenciones',
    status: 'confirmado', start_time: '09:00', end_time: '13:00', event_date: null,
    checklist: { id: 2, items: zones([
      { zone: 'Audio', names: ['Sistema de conferencia Beyerdynamic', 'Atril con micrófono', 'Traducción simultánea x3 canales'] },
      { zone: 'Video', names: ['Pantalla 4x3m', 'Proyector 15.000 lm', 'Pantalla confianza presidente', 'Switcher de presentación', 'Streaming setup', 'Grabación de video x2 cámaras'] },
      { zone: 'Varios', names: ['Sistema de votación electrónica'] },
    ]) }
  },
  {
    id: 3, name: 'Lanzamiento Otoniel Pro', client: 'Otoniel Corp', place: 'Espacio Ánima',
    status: 'confirmado', start_time: '19:30', end_time: '23:30', event_date: null,
    checklist: { id: 3, items: zones([
      { zone: 'Audio', names: ['Line array L-Acoustics K2 x8', 'Sub L-Acoustics SB28 x6', 'Consola DiGiCo SD12', 'Consola monitores DiGiCo SD9', 'Monitor Wedge x6', 'Side fill x2', 'Micrófono vocal x4', 'Micrófono solapa x6', 'DI box activo x8', 'Rack de efectos', 'Splitter de señal', 'Procesador de sistema Lake'] },
      { zone: 'Video', names: ['LED wall 8x4m resolución P3.9', 'LED wall lateral 3x2m x2', 'Procesador video Novastar', 'Laptop señal principal', 'Backup laptop', 'Cámara PTZ x3', 'Switcher ATEM 4K', 'Servidor de video Resolume', 'Cableado HDMI 4K x20m'] },
      { zone: 'Iluminación', names: ['Moving head Beam x12', 'Moving head Spot x8', 'Moving head Wash x10', 'Strobos Atomic x4', 'PAR LED x24', 'Bañador de color piso x8', 'Consola MA Lighting grandMA3', 'Dimmer rack x2', 'Hazer profesional x2', 'Cañón CO2 x4'] },
      { zone: 'Estructura', names: ['Truss cuadrado 30x30 – 40m lineales', 'Bases truss con ruedas x8', 'Motor chain hoist 500kg x6', 'Rigging kit completo'] },
      { zone: 'Backline', names: ['Batería DW x1', 'Amplificador guitarra x2', 'Amplificador bajo x1', 'Piano eléctrico x1'] },
    ]) }
  },
  {
    id: 4, name: 'Cumpleaños 15 Valentina', client: 'Familia Rodríguez', place: 'Salón Los Naranjos',
    status: 'posible', start_time: '20:00', end_time: '02:00', event_date: null,
    checklist: { id: 4, items: zones([
      { zone: 'Audio', names: ['Sistema de sonido 2x1 kw', 'Subwoofer x2', 'Micrófono inalámbrico x2', 'Consola pequeña'] },
      { zone: 'Iluminación', names: ['Iluminación pista de baile', 'Máquina de humo', 'PAR LED x8', 'Cabeza móvil x2'] },
    ]) }
  },
  {
    id: 5, name: 'Tech Summit Uruguay 2026', client: 'CUTI', place: 'Teatro El Galpón',
    status: 'confirmado', start_time: '14:00', end_time: '19:00', event_date: null,
    checklist: { id: 5, items: zones([
      { zone: 'Audio', names: ['Line array JBL VTX x6', 'Sub JBL VTX B18 x4', 'Consola Yamaha CL5', 'Procesador BSS London', 'Micrófono de cuello x4', 'Micrófono inalámbrico x3', 'Monitor in-ear x2 canales', 'Sistema de retorno personal'] },
      { zone: 'Video', names: ['Pantalla frontal 6x3m', 'Pantalla lateral x2', 'Proyector edge blending x3', 'Controlador de contenido', 'Cámara PTZ fondo x1', 'Cámara de mano x1', 'Switcher de video ATEM Mini Pro', 'Laptop presentaciones x2', 'Sistema de prompter'] },
      { zone: 'Iluminación', names: ['Foco fresnel x10', 'PAR 64 x8', 'Seguidor x1', 'Rieles de luz LED x4', 'DMX controller', 'Consola iluminación básica'] },
      { zone: 'Telecomunicaciones', names: ['Switch de red 24 puertos', 'Router dedicado evento', 'Access point x4', 'Cableado CAT6 – 100m', 'Rack de telecomunicaciones'] },
      { zone: 'Estructura', names: ['Pantallas en pie x2', 'Podio presidencial', 'Mesa de moderadores x6 sillas', 'Señalética evento', 'Backdrop impreso 4x2.5m'] },
    ]) }
  },
  {
    id: 6, name: 'Cena Anual BBVA Uruguay', client: 'BBVA', place: 'Hotel NH Columbia',
    status: 'confirmado', start_time: '20:30', end_time: '23:30', event_date: null,
    checklist: { id: 6, items: zones([
      { zone: 'Audio', names: ['Sistema de audio salón', 'Micrófono inalámbrico x2', 'Atril presidencial', 'Equipo de reproducción música'] },
      { zone: 'Video', names: ['Pantalla retráctil 3x2m', 'Proyector Full HD'] },
      { zone: 'Iluminación', names: ['Iluminación cena', 'PAR LED cálido x16', 'Controlador DMX básico'] },
      { zone: 'Varios', names: ['Cableado de señal completo'] },
    ]) }
  },
  {
    id: 7, name: 'Club Medina — DJ Set Closing', client: 'Club Medina', place: 'Club Medina',
    status: 'posible', start_time: '23:00', end_time: '06:00', event_date: null,
    checklist: { id: 7, items: zones([
      { zone: 'Audio', names: ['Sistema RCF line array x4', 'Sub RCF x4', 'CDJ 3000 x2', 'Mixer Pioneer DJM 900', 'Monitor DJ x2', 'Interfaz de audio DJ', 'Sistema de monitoreo cabina', 'Subwoofer cabina x1', 'Rack de procesamiento', 'Cableado de potencia'] },
      { zone: 'Iluminación', names: ['Controlador de luces', 'Moving head x4', 'Strobos x2', 'Máquina de humo', 'Láser x2'] },
    ]) }
  },
];
// ── fin mock ──

const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

// Ícono chico de vínculo (cadena) para marcar equipos vinculados entre eventos.
function LinkGlyph({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0, verticalAlign: '-1px' }} aria-hidden="true">
      <path d="M9 17H7A5 5 0 0 1 7 7h2" />
      <path d="M15 7h2a5 5 0 0 1 0 10h-2" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  );
}
const MONTH_NAMES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDayLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return `${DAY_NAMES[d.getDay()]} ${d.getDate()} de ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
}

// Hora de comienzo de armado. Si es un día previo al evento, antepone el día.
function formatArmado(setupDate, setupStart, eventDate) {
  const t = String(setupStart).slice(0, 5);
  const sd = setupDate ? String(setupDate).slice(0, 10) : null;
  const ed = eventDate ? String(eventDate).slice(0, 10) : null;
  if (sd && ed && sd < ed) {
    const d = new Date(sd + 'T12:00:00');
    return `${DAY_NAMES[d.getDay()]} ${t}`;
  }
  return t;
}

// Scroll horizontal continuo: se carga una ventana de días y se extiende de a
// CHUNK cuando el scroll se acerca a un borde. Cada día es un bloque en la tira.
const CHUNK = 7;
const EDGE_ZONE = 400;

function evDate(ev) { return String(ev.event_date).slice(0, 10); }

function diffDays(a, b) {
  return Math.round((new Date(a + 'T00:00:00') - new Date(b + 'T00:00:00')) / 86400000);
}

function mergeEvents(prev, incoming) {
  const seen = new Set(prev.map(e => e.id));
  const merged = [...prev, ...incoming.filter(e => !seen.has(e.id))];
  merged.sort((a, b) =>
    evDate(a).localeCompare(evDate(b)) ||
    String(a.start_time || '99').localeCompare(String(b.start_time || '99')) ||
    a.id - b.id
  );
  return merged;
}

export default function EquipmentPlanningPage() {
  const { user } = useAuth();
  const canEdit = canWrite(user, 'agenda.equipos');
  const navigate = useNavigate();
  const [range, setRange] = useState(() => ({ from: addDays(todayStr(), -3), to: addDays(todayStr(), 13) }));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  // Día visible en la tira — lo actualiza el scroll-spy y lo muestra el toolbar
  const [visibleDate, setVisibleDate] = useState(todayStr);
  const [activeItemId, setActiveItemId] = useState(null);
  const [conflictsByEventId, setConflictsByEventId] = useState({});
  const [groupCtxByEventId, setGroupCtxByEventId] = useState({}); // vinculación entre eventos
  const [linkMode, setLinkMode] = useState(null); // { target: event, source: event } — vista lado a lado
  const stripRef = useRef(null);
  const rangeRef = useRef(range);
  const extendingRef = useRef(false);
  const pendingScrollRef = useRef({ date: todayStr(), behavior: 'auto' });
  const prependWidthRef = useRef(null);
  const conflictsFetchedRef = useRef(new Set());
  const rafRef = useRef(0);

  useEffect(() => { rangeRef.current = range; }, [range]);
  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const flatItemIds = useMemo(
    () => events.flatMap(ev => (ev.checklist?.items ?? []).map(it => it.id)),
    [events]
  );

  const moveToNext = useCallback((currentId) => {
    const idx = flatItemIds.indexOf(currentId);
    if (idx >= 0 && idx < flatItemIds.length - 1) setActiveItemId(flatItemIds[idx + 1]);
  }, [flatItemIds]);

  const moveToPrev = useCallback((currentId) => {
    const idx = flatItemIds.indexOf(currentId);
    if (idx > 0) setActiveItemId(flatItemIds[idx - 1]);
  }, [flatItemIds]);

  const fetchDays = useCallback(async (from, to) => {
    const { data } = await api.get(`/checklists/planning?from=${from}&to=${to}`);
    return data;
  }, []);

  useEffect(() => {
    let alive = true;
    if (MOCK_MODE) {
      setTimeout(() => {
        setEvents(MOCK_EVENTS.map(ev => ({ ...ev, event_date: todayStr() })));
        setLoading(false);
      }, 200);
      return;
    }
    (async () => {
      try {
        const data = await fetchDays(rangeRef.current.from, rangeRef.current.to);
        if (alive) setEvents(data);
      } catch (e) { console.error(e); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [fetchDays]);

  // Tiempo real: al asignar/quitar equipo (u otra mutación de agenda), re-cargar
  // los datos del rango actual sin tocar el scroll ni la ventana de días.
  useEffect(() => {
    if (MOCK_MODE) return;
    const socket = io('/', { withCredentials: true });
    let t = null;
    socket.on('agenda:changed', () => {
      clearTimeout(t);
      t = setTimeout(async () => {
        try {
          const data = await fetchDays(rangeRef.current.from, rangeRef.current.to);
          setEvents(data);
        } catch (e) { console.error(e); }
      }, 400);
    });
    return () => { clearTimeout(t); socket.disconnect(); };
  }, [fetchDays]);

  const extend = useCallback(async (dir) => {
    if (extendingRef.current || MOCK_MODE) return;
    extendingRef.current = true;
    const { from, to } = rangeRef.current;
    try {
      if (dir === 'left') {
        const newFrom = addDays(from, -CHUNK);
        const data = await fetchDays(newFrom, addDays(from, -1));
        // Al prepender días el contenido corre hacia la derecha: se guarda el
        // ancho previo para compensar scrollLeft en el layout effect.
        prependWidthRef.current = stripRef.current?.scrollWidth ?? null;
        setEvents(prev => mergeEvents(prev, data));
        setRange(r => ({ ...r, from: newFrom }));
      } else {
        const newTo = addDays(to, CHUNK);
        const data = await fetchDays(addDays(to, 1), newTo);
        setEvents(prev => mergeEvents(prev, data));
        setRange(r => ({ ...r, to: newTo }));
      }
    } catch (e) { console.error(e); }
    finally { extendingRef.current = false; }
  }, [fetchDays]);

  useLayoutEffect(() => {
    if (prependWidthRef.current == null) return;
    const el = stripRef.current;
    if (el) el.scrollLeft += el.scrollWidth - prependWidthRef.current;
    prependWidthRef.current = null;
  }, [range.from]);

  const days = useMemo(() => {
    const byDay = {};
    for (const ev of events) (byDay[evDate(ev)] = byDay[evDate(ev)] || []).push(ev);
    const list = [];
    for (let d = range.from; d <= range.to; d = addDays(d, 1)) {
      list.push({ date: d, events: byDay[d] || [] });
    }
    return list;
  }, [events, range]);

  // El scroll "smooth" nativo puede estar deshabilitado por flags del navegador
  // (scrollTo/scrollIntoView quedan no-op) — se anima a mano con rAF.
  // El destino se recalcula EN CADA FRAME desde el nodo vivo: si la extensión
  // hacia atrás prepende días y corre el contenido, un destino fijo dejaría la
  // animación peleando contra el ajuste de scrollLeft en loop infinito.
  const animRef = useRef(0);
  const scrollStripTo = useCallback((d, behavior) => {
    const el = stripRef.current;
    if (!el) return;
    cancelAnimationFrame(animRef.current);
    const targetFor = () => {
      const node = el.querySelector(`.ep-day[data-date="${d}"]`);
      if (!node) return null;
      return Math.max(0, el.scrollLeft + node.getBoundingClientRect().left - el.getBoundingClientRect().left - 16);
    };
    // Con el tab oculto el navegador no dispara rAF — saltar sin animar.
    if (behavior !== 'smooth' || document.hidden) {
      const t = targetFor();
      if (t != null) el.scrollLeft = t;
      return;
    }
    const t0 = performance.now();
    const step = (now) => {
      const target = targetFor();
      if (target == null) return;
      const remaining = target - el.scrollLeft;
      if (Math.abs(remaining) < 1 || now - t0 > 1200) { el.scrollLeft = target; return; }
      el.scrollLeft += remaining * 0.18;
      animRef.current = requestAnimationFrame(step);
    };
    animRef.current = requestAnimationFrame(step);
  }, []);
  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  const goToDate = useCallback((d, behavior = 'smooth') => {
    const { from, to } = rangeRef.current;
    if (d < from || d > to) {
      // Fuera de la ventana cargada: recentrar en el día pedido
      const newFrom = addDays(d, -3);
      const newTo = addDays(d, 13);
      pendingScrollRef.current = { date: d, behavior: 'auto' };
      conflictsFetchedRef.current = new Set();
      setConflictsByEventId({});
      setVisibleDate(d);
      setLoading(true);
      fetchDays(newFrom, newTo)
        .then(data => { setEvents(data); setRange({ from: newFrom, to: newTo }); })
        .catch(console.error)
        .finally(() => setLoading(false));
      return;
    }
    pendingScrollRef.current = null;
    setVisibleDate(d);
    scrollStripTo(d, behavior);
  }, [fetchDays, scrollStripTo]);

  // Scroll pendiente (carga inicial / salto fuera de ventana): corre cuando el
  // bloque del día objetivo ya está montado.
  useLayoutEffect(() => {
    const p = pendingScrollRef.current;
    if (!p || loading) return;
    if (!stripRef.current?.querySelector(`.ep-day[data-date="${p.date}"]`)) return;
    pendingScrollRef.current = null;
    scrollStripTo(p.date, p.behavior);
  }, [loading, days, scrollStripTo]);

  const spyAndExtend = () => {
    const el = stripRef.current;
    if (!el) return;
    // Scroll-spy: el día activo es el que cubre el borde izquierdo visible
    const edge = el.getBoundingClientRect().left + 80;
    let current = null;
    for (const node of el.querySelectorAll('.ep-day')) {
      if (node.getBoundingClientRect().left <= edge) current = node.dataset.date; else break;
    }
    if (current) setVisibleDate(current);
    if (el.scrollLeft < EDGE_ZONE) extend('left');
    else if (el.scrollLeft + el.clientWidth > el.scrollWidth - EDGE_ZONE) extend('right');
  };

  const onStripScroll = () => {
    // rAF como throttle — con el tab oculto no dispara, se corre directo
    if (document.hidden) { spyAndExtend(); return; }
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      spyAndExtend();
    });
  };

  const isToday = visibleDate === todayStr();

  // Conflictos: se piden para los eventos del día visible ± 2 (bajo demanda al
  // scrollear), no para toda la ventana cargada.
  useEffect(() => {
    if (MOCK_MODE) return;
    const missing = events.filter(ev =>
      ev.checklist?.id &&
      Math.abs(diffDays(evDate(ev), visibleDate)) <= 2 &&
      !conflictsFetchedRef.current.has(ev.id)
    );
    if (!missing.length) return;
    missing.forEach(ev => conflictsFetchedRef.current.add(ev.id));
    let alive = true;
    Promise.allSettled(missing.map(ev => api.get(`/inventory/event-conflicts?event_id=${ev.id}`)))
      .then(results => {
        if (!alive) return;
        setConflictsByEventId(prev => {
          const next = { ...prev };
          results.forEach((r, i) => { if (r.status === 'fulfilled') next[missing[i].id] = r.value.data; });
          return next;
        });
      });
    Promise.allSettled(missing.map(ev => api.get(`/links/group-context?event_id=${ev.id}`)))
      .then(results => {
        if (!alive) return;
        setGroupCtxByEventId(prev => {
          const next = { ...prev };
          results.forEach((r, i) => { if (r.status === 'fulfilled') next[missing[i].id] = r.value.data; });
          return next;
        });
      });
    return () => { alive = false; };
  }, [events, visibleDate]);

  const refreshConflicts = useCallback(async () => {
    // Los eventos del modo vincular se incluyen SIEMPRE, estén donde estén en
    // el calendario: si no, este replace total pisa (en carrera con
    // refreshLinkModeData) los conflictos recién traídos para el popup y el
    // chip espejado pierde la advertencia de sobre-asignación.
    const inLinkMode = (ev) => linkModeRef.current
      && (ev.id === linkModeRef.current.targetId || ev.id === linkModeRef.current.sourceId);
    const nearby = events.filter(ev =>
      ev.checklist?.id && (Math.abs(diffDays(evDate(ev), visibleDate)) <= 2 || inLinkMode(ev))
    );
    // Los días lejanos se invalidan: se re-piden cuando se scrollea hasta ellos
    conflictsFetchedRef.current = new Set(nearby.map(ev => ev.id));
    if (!nearby.length) { setConflictsByEventId({}); return; }
    const results = await Promise.allSettled(
      nearby.map(ev => api.get(`/inventory/event-conflicts?event_id=${ev.id}`))
    );
    const byId = {};
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') byId[nearby[i].id] = r.value.data;
    });
    setConflictsByEventId(byId);
    const gctx = await Promise.allSettled(
      nearby.map(ev => api.get(`/links/group-context?event_id=${ev.id}`))
    );
    const gById = {};
    gctx.forEach((r, i) => { if (r.status === 'fulfilled') gById[nearby[i].id] = r.value.data; });
    setGroupCtxByEventId(gById);
  }, [events, visibleDate]);

  const updateItemLinks = (eventId, itemId, newLinks) => {
    setEvents(prev => prev.map(ev => {
      if (ev.id !== eventId || !ev.checklist) return ev;
      return {
        ...ev,
        checklist: {
          ...ev.checklist,
          items: ev.checklist.items.map(it =>
            it.id === itemId ? { ...it, links: newLinks } : it
          ),
        },
      };
    }));
    refreshConflicts();
  };

  const addEventItem = (eventId, newItem) => {
    setEvents(prev => prev.map(ev => {
      if (ev.id !== eventId || !ev.checklist) return ev;
      return { ...ev, checklist: { ...ev.checklist, items: [...ev.checklist.items, newItem] } };
    }));
  };

  // Refresco completo del rango cargado — usado tras renombrar/eliminar una
  // sección (sector o categoría), que puede tocar muchos ítems y notas a la
  // vez y no vale la pena reconciliar a mano en el estado local.
  const refreshEvents = useCallback(async () => {
    if (MOCK_MODE) return;
    try {
      const data = await fetchDays(rangeRef.current.from, rangeRef.current.to);
      setEvents(data);
    } catch (e) { console.error(e); }
  }, [fetchDays]);

  const deleteEventItem = async (eventId, itemId) => {
    const ev = events.find(e => e.id === eventId);
    const checklistId = ev?.checklist?.id;
    if (!checklistId) return;
    try {
      await api.delete(`/checklists/${checklistId}/items/${itemId}`);
      setEvents(prev => prev.map(e => {
        if (e.id !== eventId || !e.checklist) return e;
        return { ...e, checklist: { ...e.checklist, items: e.checklist.items.filter(it => it.id !== itemId) } };
      }));
      refreshConflicts();
    } catch (err) { console.error(err); }
  };

  // ── Modo vinculación (popup con dos eventos flotando) ──
  const enterLinkMode = useCallback((targetEvent) => {
    setLinkMode({ targetId: targetEvent.id, sourceId: null });
  }, []);
  const exitLinkMode = useCallback(() => setLinkMode(null), []);
  const setLinkSource = useCallback((sourceId) => {
    setLinkMode(m => m ? { ...m, sourceId: sourceId || null } : m);
  }, []);
  useEffect(() => {
    if (!linkMode) return;
    const onKey = e => { if (e.key === 'Escape') exitLinkMode(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [linkMode, exitLinkMode]);
  // Eventos frescos (con los links/grupos actualizados) para el popup.
  const linkModeTarget = useMemo(
    () => linkMode ? events.find(e => e.id === linkMode.targetId) || null : null,
    [linkMode, events]
  );
  const linkModeSource = useMemo(
    () => linkMode?.sourceId ? events.find(e => e.id === linkMode.sourceId) || null : null,
    [linkMode, events]
  );
  // Candidatos ±5 días del evento destino para el selector del popup.
  const linkCandidates = useMemo(() => {
    if (!linkModeTarget?.event_date) return [];
    return events
      .filter(e => e.id !== linkModeTarget.id && e.event_date && Math.abs(diffDays(evDate(e), evDate(linkModeTarget))) <= 5)
      .sort((a, b) => evDate(a).localeCompare(evDate(b)));
  }, [events, linkModeTarget]);

  // Los eventos del popup pueden estar fuera del ±2 días que fetchea el kanban,
  // así que pedimos su group-context (badges "viene de/se queda para") y sus
  // conflictos aparte, tanto al abrir como tras vincular/desvincular.
  const refreshLinkModeData = useCallback(async (ids) => {
    const list = [...new Set(ids)].filter(Boolean);
    if (!list.length) return;
    const [gc, cf] = await Promise.all([
      Promise.allSettled(list.map(id => api.get(`/links/group-context?event_id=${id}`))),
      Promise.allSettled(list.map(id => api.get(`/inventory/event-conflicts?event_id=${id}`))),
    ]);
    setGroupCtxByEventId(prev => { const n = { ...prev }; gc.forEach((r, i) => { if (r.status === 'fulfilled') n[list[i]] = r.value.data; }); return n; });
    setConflictsByEventId(prev => { const n = { ...prev }; cf.forEach((r, i) => { if (r.status === 'fulfilled') n[list[i]] = r.value.data; }); return n; });
  }, []);
  const linkModeRef = useRef(linkMode);
  linkModeRef.current = linkMode;
  useEffect(() => {
    if (linkMode) refreshLinkModeData([linkMode.targetId, linkMode.sourceId]);
  }, [linkMode?.targetId, linkMode?.sourceId, refreshLinkModeData]); // eslint-disable-line

  const linkLine = useCallback(async (sourceItemId, targetItemId) => {
    try {
      await api.post('/links/link-line', { source_checklist_item_id: sourceItemId, target_checklist_item_id: targetItemId });
      const data = await fetchDays(rangeRef.current.from, rangeRef.current.to);
      setEvents(data);
      refreshConflicts();
      if (linkModeRef.current) refreshLinkModeData([linkModeRef.current.targetId, linkModeRef.current.sourceId]);
    } catch (err) { console.error(err); }
  }, [fetchDays, refreshConflicts, refreshLinkModeData]);

  // Drag por pointer events (no HTML5 native DnD, que es frágil y no anda en
  // touch): al arrastrar una línea, se sigue el cursor con un "ghost" y se
  // resalta la línea destino; al soltar sobre otra línea, se vincula.
  const onLineDragStart = useCallback((lineId, lineName, e) => {
    if (e.button != null && e.button !== 0) return; // solo botón primario
    // Los controles dentro de la línea (✕ de chips, borrar, inputs) siguen
    // operando en modo vincular: el preventDefault de abajo suprimiría sus
    // eventos de mouse y quedarían muertos.
    if (e.target.closest('button, input, textarea')) return;
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    let started = false;
    let ghost = null;
    const clearHi = () => document.querySelectorAll('.ep-item--dragover').forEach(el => el.classList.remove('ep-item--dragover'));
    const targetAt = (x, y) => {
      const el = document.elementFromPoint(x, y)?.closest('[data-line-id]');
      if (!el) return null;
      const id = Number(el.dataset.lineId);
      return id && id !== lineId ? { el, id } : null;
    };
    const onMove = (ev) => {
      if (!started) {
        if (Math.abs(ev.clientX - startX) < 4 && Math.abs(ev.clientY - startY) < 4) return;
        started = true;
        document.body.style.userSelect = 'none';
        ghost = document.createElement('div');
        ghost.className = 'ep-drag-ghost';
        ghost.textContent = lineName || 'Equipo';
        document.body.appendChild(ghost);
      }
      ghost.style.left = ev.clientX + 12 + 'px';
      ghost.style.top = ev.clientY + 12 + 'px';
      clearHi();
      targetAt(ev.clientX, ev.clientY)?.el.classList.add('ep-item--dragover');
    };
    const onUp = (ev) => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.body.style.userSelect = '';
      if (ghost) ghost.remove();
      clearHi();
      if (!started) return;
      const t = targetAt(ev.clientX, ev.clientY);
      if (t) linkLine(lineId, t.id);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [linkLine]);

  const unlinkLine = useCallback(async (itemId, dissolve = false) => {
    try {
      await api.post('/links/unlink-line', { checklist_item_id: itemId, dissolve });
      const data = await fetchDays(rangeRef.current.from, rangeRef.current.to);
      setEvents(data);
      refreshConflicts();
      if (linkModeRef.current) refreshLinkModeData([linkModeRef.current.targetId, linkModeRef.current.sourceId]);
    } catch (err) { console.error(err); }
  }, [fetchDays, refreshConflicts, refreshLinkModeData]);

  const [search, setSearch] = useState('');
  const [searchDims, setSearchDims] = useState(EQ_DEFAULT_DIMENSIONS);
  const toggleSearchDim = (key) => setSearchDims(prev => (
    prev.includes(key) ? prev.filter(d => d !== key) : [...prev, key]
  ));
  const eqSearch = useEquipmentSearch({ query: search, dimensions: searchDims });

  // El tablero es una linea de tiempo, asi que la busqueda no reordena: agrupa
  // los eventos que coinciden por fecha y deja los mismos dias en el mismo
  // sentido, solo que sin los que no tienen nada.
  const searchDays = useMemo(() => {
    if (!eqSearch.active) return [];
    const byDate = new Map();
    for (const ev of eqSearch.events) {
      const d = String(ev.event_date || '').slice(0, 10);
      if (!d) continue;
      if (!byDate.has(d)) byDate.set(d, []);
      byDate.get(d).push(ev);
    }
    return [...byDate.entries()].sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, evs]) => ({ date, events: evs }));
  }, [eqSearch.active, eqSearch.events]);

  // Sin esto la tira abre a la izquierda, o sea en lo mas viejo de la pagina, y
  // parece que la busqueda solo encontro eventos pasados. El backend ya dice
  // cual es la primera coincidencia de hoy en adelante: se scrollea ahi.
  useEffect(() => {
    if (!eqSearch.active || !eqSearch.firstUpcomingDate) return;
    const strip = stripRef.current;
    if (!strip) return;
    const id = requestAnimationFrame(() => {
      const el = strip.querySelector(`[data-date="${eqSearch.firstUpcomingDate}"]`);
      if (el) strip.scrollLeft = el.offsetLeft - 16;
    });
    return () => cancelAnimationFrame(id);
  }, [eqSearch.active, eqSearch.firstUpcomingDate, searchDays]);

  const renderEventCol = (ev, extra = {}) => (
    <EventCol
      key={ev.id}
      event={ev}
      onLinksChange={(itemId, links) => updateItemLinks(ev.id, itemId, links)}
      onItemAdded={item => addEventItem(ev.id, item)}
      onDeleteItem={itemId => deleteEventItem(ev.id, itemId)}
      onSectionsChanged={refreshEvents}
      activeItemId={activeItemId}
      onActivate={setActiveItemId}
      onConsolidated={moveToNext}
      onArrowOut={(itemId, dir) => dir === 'down' ? moveToNext(itemId) : moveToPrev(itemId)}
      conflictsData={conflictsByEventId[ev.id]}
      groupCtx={groupCtxByEventId[ev.id]}
      onConflictEventClick={(c) => goToDate(String(c.event_date).slice(0, 10))}
      allEvents={events}
      onEnterLinkMode={enterLinkMode}
      inLinkMode={false}
      onLinkLine={linkLine}
      onUnlinkLine={unlinkLine}
      canEdit={canEdit}
      onLineDragStart={onLineDragStart}
      {...extra}
    />
  );

  return (
    <div className="ep-page">
      <div className="ep-toolbar">
        <div className="ep-toolbar-left">
          <button className="ep-back-btn" onClick={() => navigate('/')}>← Agenda</button>
          <h1 className="ep-title">Vista de Equipos</h1>
        </div>
        {/* Los resultados abarcan cualquier fecha, así que el día que estaba
            mirando el tablero deja de significar algo: en su lugar va cuántos
            eventos cayeron. */}
        {eqSearch.active ? (
          <div className="ep-search-summary">
            {eqSearch.loading ? 'Buscando…' : (
              <>
                <strong>{eqSearch.total}</strong>
                {eqSearch.total === 1 ? ' evento' : ' eventos'}
                {eqSearch.hasMore && <span className="ep-search-summary-sub">
                  · mostrando {eqSearch.events.length}
                </span>}
              </>
            )}
          </div>
        ) : (
          <div className="ep-day-nav">
            <button className="ep-nav-btn" onClick={() => goToDate(addDays(visibleDate, -1))}>‹</button>
            <span className={`ep-day-label${isToday ? ' ep-day-label--today' : ''}`}>
              {formatDayLabel(visibleDate)}
            </span>
            <button className="ep-nav-btn ep-nav-today" onClick={() => goToDate(todayStr())}>HOY</button>
            <button className="ep-nav-btn" onClick={() => goToDate(addDays(visibleDate, 1))}>›</button>
          </div>
        )}
        <div className="ep-search">
          <input
            className="ep-search-input"
            type="search"
            placeholder="Buscar equipo, evento o lugar…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search.trim() !== '' && (
            <div className="ep-search-chips">
              {EQ_DIMENSIONS.map(d => (
                <button
                  key={d.key}
                  type="button"
                  className={`ep-chip${searchDims.includes(d.key) ? ' is-on' : ''}`}
                  aria-pressed={searchDims.includes(d.key)}
                  onClick={() => toggleSearchDim(d.key)}
                >{d.label}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="ep-kanban" ref={stripRef} onScroll={eqSearch.active ? undefined : onStripScroll}>
        {(eqSearch.active ? eqSearch.loading : loading) ? (
          <div className="ep-loading"><div className="spinner" /></div>
        ) : eqSearch.active && searchDays.length === 0 ? (
          <div className="ep-search-empty">Ningun equipo, evento ni lugar coincide con “{search.trim()}”.</div>
        ) : (
          (eqSearch.active ? searchDays : days).map(({ date: d, events: dayEvents }) => (
            <section
              key={d}
              data-date={d}
              className={`ep-day${d === todayStr() ? ' ep-day--today' : ''}${d < todayStr() ? ' ep-day--past' : ''}${dayEvents.length === 0 ? ' ep-day--empty' : ''}${Math.floor(Date.parse(d + 'T00:00:00Z') / 86400000) % 2 ? ' ep-day--alt' : ''}`}
            >
              <div className="ep-day-title">
                <span className="ep-day-title-text">{formatDayLabel(d)}</span>
                {d === todayStr() && <span className="ep-day-today-tag">HOY</span>}
              </div>
              <div className="ep-day-body">
                {dayEvents.length === 0 ? (
                  <div className="ep-day-empty-msg">Sin eventos</div>
                ) : (
                  dayEvents.map(ev => renderEventCol(ev, ev.match ? { matchInfo: ev.match } : {}))
                )}
              </div>
            </section>
          ))
        )}
        {eqSearch.active && eqSearch.hasMore && (
          <div className="ep-more">
            <button className="ep-more-btn" onClick={eqSearch.loadMore} disabled={eqSearch.loadingMore}>
              {eqSearch.loadingMore ? 'Cargando…' : `Cargar más (${eqSearch.total - eqSearch.events.length})`}
            </button>
          </div>
        )}
      </div>

      {linkMode && linkModeTarget && (
        <div className="ep-linkmodal-backdrop" onClick={exitLinkMode}>
          <div className="ep-linkmodal" onClick={e => e.stopPropagation()}>
            <div className="ep-linkmodal-head">
              <span className="ep-linkmodal-hint">
                <LinkGlyph /> Arrastrá un equipo de un evento al otro para vincularlo
              </span>
              <button className="ep-linkmodal-close" onClick={exitLinkMode} title="Cerrar" aria-label="Cerrar">✕</button>
            </div>
            <div className="ep-linkmodal-cols">
              <div className="ep-linkmode-col">
                <div className="ep-linkmode-col-tag">Tu evento</div>
                {renderEventCol(linkModeTarget, { inLinkMode: true })}
              </div>
              <div className="ep-linkmode-col">
                <div className="ep-linkmode-col-tag">
                  Otro evento
                  <select
                    className="ep-linkmodal-select"
                    value={linkMode.sourceId || ''}
                    onChange={e => setLinkSource(e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">Elegí un evento…</option>
                    {linkCandidates.map(c => (
                      <option key={c.id} value={c.id}>{formatDayLabel(evDate(c))} · {c.name}</option>
                    ))}
                  </select>
                </div>
                {linkModeSource ? (
                  renderEventCol(linkModeSource, { inLinkMode: true })
                ) : (
                  <div className="ep-linkmodal-empty">
                    {linkCandidates.length
                      ? 'Elegí un evento ±5 días para comparar y vincular equipos.'
                      : 'No hay eventos cerca (±5 días) para vincular.'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NotePopup({ cat, note, anchorRect, onSave, onClose }) {
  const [text, setText] = useState(note || '');
  const taRef = useRef(null);

  useEffect(() => { taRef.current?.focus(); }, []);

  const confirm = () => { onSave(text.trim()); onClose(); };

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const W = 260;
  let left = anchorRect.left;
  if (left + W > vw - 8) left = vw - W - 8;
  const spaceBelow = vh - anchorRect.bottom - 8;
  const popupH = 130;
  const top = spaceBelow >= popupH ? anchorRect.bottom + 6 : anchorRect.top - popupH - 6;

  return ReactDOM.createPortal(
    <>
      <div className="ep-note-backdrop" onMouseDown={confirm} />
      <div className="ep-note-popup" style={{ left, top, width: W }}>
        <div className="ep-note-popup-label">{cat || 'General'}</div>
        <textarea
          ref={taRef}
          className="ep-note-popup-ta"
          placeholder="Nota para esta categoría…"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); confirm(); }
            if (e.key === 'Escape') { onClose(); }
          }}
          rows={3}
        />
        <div className="ep-note-popup-hint">Enter para confirmar · Esc para cerrar</div>
      </div>
    </>,
    document.body
  );
}


function CategorySection({
  zone, cat, items, note, onSaveNote, onAddItem, onDeleteItem, onLinksChange, activeItemId, onActivate,
  onConsolidated, onArrowOut, conflictsData, groupCtx, onConflictEventClick, committedIds, eventId,
  inLinkMode, onLinkLine, onUnlinkLine, canEdit, onLineDragStart,
  pendingAdd, onConsumePendingAdd,
  isRenaming, renameValue, onRenameChange, onRenameKeyDown, onRenameBlur, renameInputRef,
  onStartRename, onDeleteSection, showSectionActions, matchedIds,
}) {
  const [popup, setPopup] = useState(null);
  const [searchOpenId, setSearchOpenId] = useState(null);
  const [addingItem, setAddingItem] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    if (!pendingAdd) return;
    if ((pendingAdd.zone || '') === (zone || '') && (pendingAdd.category || '') === (cat || '')) {
      setAddingItem(true);
      onConsumePendingAdd?.();
    }
  }, [pendingAdd]); // eslint-disable-line

  const crossConflicts = useMemo(() => {
    if (!conflictsData?.by_name) return {};
    const result = {};
    for (const [name, c] of Object.entries(conflictsData.by_name)) {
      if ((c.soft_conflict || 0) + (c.en_uso || 0) > 0)
        result[name] = (c.soft_conflict || 0) + (c.en_uso || 0);
    }
    return result;
  }, [conflictsData]);
  const noteBtn = useRef(null);
  const addRef = useRef(null);

  useEffect(() => { if (addingItem && addRef.current) addRef.current.focus(); }, [addingItem]);

  const openNote = () => {
    const rect = noteBtn.current?.getBoundingClientRect();
    if (rect) setPopup(rect);
  };

  // Enter agrega y deja el input abierto/enfocado para seguir cargando en
  // cadena; Enter vacío o Escape cierran.
  const commitAddInline = async () => {
    const name = newItemName.trim();
    setAddingItem(false);
    setNewItemName('');
    if (name) await onAddItem(cat, name);
  };

  return (
    <div className="ep-zone">
      <div className="ep-zone-header">
        <div className="ep-zone-title">
          {isRenaming ? (
            <input
              ref={renameInputRef}
              className="ep-section-rename-input"
              value={renameValue}
              onChange={onRenameChange}
              onKeyDown={onRenameKeyDown}
              onBlur={onRenameBlur}
            />
          ) : (
            <span className="ep-zone-name">{cat || 'General'}</span>
          )}
          {canEdit && showSectionActions && !isRenaming && (
            <div className="ep-section-actions">
              <button className="ep-section-action-btn" title="Renombrar categoría" aria-label="Renombrar categoría" onClick={onStartRename}><IconPencil /></button>
              <button className="ep-section-action-btn" title="Eliminar categoría" aria-label="Eliminar categoría" onClick={onDeleteSection}><IconTrash /></button>
            </div>
          )}
        </div>
        {canEdit && (
          <div className="ep-zone-actions">
            <button ref={noteBtn} className="ep-zone-icon-btn ep-zone-icon-btn--note" onClick={openNote} title="Nota">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </button>
            <button className="ep-zone-icon-btn ep-zone-icon-btn--add" onClick={() => setAddingItem(true)} title="Agregar equipo">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </button>
          </div>
        )}
      </div>
      {note && (
        <div className="ep-note-wrap">
          <div className="ep-note-line" onClick={canEdit ? openNote : undefined}>{note}</div>
        </div>
      )}
      {canEdit && popup && (
        <NotePopup
          cat={cat}
          note={note}
          anchorRect={popup}
          onSave={(text) => { if (text !== (note || '')) onSaveNote(cat, text); }}
          onClose={() => setPopup(null)}
        />
      )}
      {items.map(item => {
        const groupedLinks = (() => {
          const map = new Map();
          for (const l of item.links || []) {
            const key = l.pack_name || l.item_name || '';
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(l);
          }
          return [...map.entries()].map(([label, group]) => ({ label, group }));
        })();
        const removeLinks = async (toRemove) => {
          try {
            // Secuencial: si la línea está vinculada, cada baja re-evalúa si el
            // grupo debe disolverse; en paralelo podrían pisarse ese chequeo.
            for (const l of toRemove) await api.delete(`/links/checklist-item/${item.id}/${l.id}`);
            const ids = new Set(toRemove.map(l => l.id));
            onLinksChange(item.id, (item.links || []).filter(l => !ids.has(l.id)));
          } catch (e) { console.error(e); }
        };
        const linkCtx = groupCtx?.[item.id];
        const isLinked = (item.links || []).some(l => l.link_group_id);
        const dnd = inLinkMode ? {
          'data-line-id': item.id,
          onPointerDown: (e) => onLineDragStart?.(item.id, item.current_name, e),
        } : {};
        return (
          <div key={item.id} data-item-id={item.id} className={`ep-item${inLinkMode ? ' ep-item--linkable' : ''}${matchedIds?.has(item.id) ? ' ep-item--match' : ''}`} {...dnd}>
            <div className="ep-item-stock">
              <div
                className="ep-item-badges"
                onClick={canEdit ? () => setSearchOpenId(id => id === item.id ? null : item.id) : undefined}
                title="Buscar en inventario"
              >
                {groupedLinks.length > 0 ? (
                  <div className="ic-chips">
                    {groupedLinks.map(({ label, group }) => (
                      <StockChip
                        key={label}
                        label={label}
                        group={group}
                        linked={group.some(l => l.link_group_id)}
                        conflict={conflictsData?.by_name?.[label]}
                        onRemove={canEdit ? () => removeLinks(group) : undefined}
                        onEventClick={onConflictEventClick}
                      />
                    ))}
                  </div>
                ) : (
                  <span className="ep-item-nostock">sin stock</span>
                )}
                {isLinked && (
                  <div className="ep-link-badges">
                    {linkCtx?.comes_from && (
                      <span className="ep-link-badge" title={`Viene de ${linkCtx.comes_from.event_name}`}>
                        <LinkGlyph /> viene de {linkCtx.comes_from.event_name}
                      </span>
                    )}
                    {linkCtx?.stays_for && (
                      <span className="ep-link-badge" title={`Se queda para ${linkCtx.stays_for.event_name}`}>
                        <LinkGlyph /> se queda para {linkCtx.stays_for.event_name}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {canEdit && <InlineConsolidator
                checklistItemId={item.id}
                links={item.links}
                onLinksChange={newLinks => onLinksChange(item.id, newLinks)}
                itemName={item.current_name}
                isActive={activeItemId === item.id}
                onActivate={() => onActivate(item.id)}
                onConsolidated={() => onConsolidated(item.id)}
                onArrowOut={dir => onArrowOut(item.id, dir)}
                committedIds={committedIds}
                crossConflicts={crossConflicts}
                eventId={eventId}
                noChips
                collapsible
                open={searchOpenId === item.id}
                onOpenChange={o => setSearchOpenId(o ? item.id : null)}
              />}
            </div>
            <div className="ep-item-name">
              <span>{item.current_name}</span>
            </div>
            <div className="ep-item-delete">
              {canEdit && <button
                className="ep-item-delete-btn"
                onClick={() => setConfirmDelete(item)}
                title="Borrar equipo"
              >✕</button>}
            </div>
          </div>
        );
      })}
      {canEdit && addingItem && (
        <div className="ep-add-item-row">
          <input
            ref={addRef}
            className="ep-add-item-input"
            placeholder="Nombre del equipo…"
            value={newItemName}
            onChange={e => setNewItemName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const name = newItemName.trim();
                if (!name) { setAddingItem(false); setNewItemName(''); return; }
                setNewItemName('');
                onAddItem(cat, name).then(() => addRef.current?.focus());
              }
              if (e.key === 'Escape') { setAddingItem(false); setNewItemName(''); }
            }}
            onBlur={commitAddInline}
          />
        </div>
      )}
      {confirmDelete && (
        <ConfirmDialog
          title="Borrar línea de equipo"
          message={`Se quita "${confirmDelete.current_name?.trim() || 'esta línea'}" y el equipo que tenga asignado. No se puede deshacer.`}
          confirmLabel="Borrar"
          danger
          onConfirm={() => { onDeleteItem(confirmDelete.id); setConfirmDelete(null); }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

// Una lista larga no avisa que sigue: la barra del navegador es finita y en
// muchos sistemas ni aparece hasta que scrolleas. Esto marca el borde de abajo
// mientras quede contenido, y se apaga solo al llegar al final.
function useHayMasAbajo(ref, deps) {
  const [hayMas, setHayMas] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const medir = () => {
      const restante = el.scrollHeight - el.scrollTop - el.clientHeight;
      setHayMas(restante > 4);
    };
    medir();
    el.addEventListener('scroll', medir, { passive: true });
    // El alto cambia al agregar o borrar equipos, no solo al scrollear.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null;
    if (ro) { ro.observe(el); for (const hijo of el.children) ro.observe(hijo); }
    return () => { el.removeEventListener('scroll', medir); if (ro) ro.disconnect(); };
  }, deps);
  return hayMas;
}

function EventCol({ event, onLinksChange, onItemAdded, onDeleteItem, activeItemId, onActivate, onConsolidated, onArrowOut, conflictsData, groupCtx, onConflictEventClick, allEvents = [], onEnterLinkMode, inLinkMode, onLinkLine, onUnlinkLine, canEdit, onLineDragStart, onSectionsChanged, matchInfo = null }) {
  const bodyRef = useRef(null);
  const hayMasAbajo = useHayMasAbajo(bodyRef, [event.checklist?.items?.length, matchInfo]);
  const matchedIds = useMemo(() => new Set(matchInfo?.itemIds || []), [matchInfo]);

  // La lista se muestra entera con su scroll de siempre; lo unico que cambia
  // en modo busqueda es donde arranca ese scroll. Se calcula la posicion en
  // vez de usar scrollIntoView, que ademas del scroller interno arrastraria la
  // tira horizontal y movería el tablero entero de lugar.
  useEffect(() => {
    const body = bodyRef.current;
    if (!body || !matchInfo || !matchInfo.itemIds?.length) return;
    const target = body.querySelector(`[data-item-id="${matchInfo.itemIds[0]}"]`);
    if (!target) return;
    const centrado = target.offsetTop - body.clientHeight / 2 + target.offsetHeight / 2;
    body.scrollTop = Math.max(0, centrado);
  }, [matchInfo]);
  // ¿Hay eventos ±5 días para ofrecer vincular? (habilita el botón)
  const hasLinkCandidates = useMemo(() => {
    if (!event.event_date) return false;
    return allEvents.some(e => e.id !== event.id && e.event_date && Math.abs(diffDays(evDate(e), evDate(event))) <= 5);
  }, [allEvents, event]);
  const { checklist } = event;

  const committedIds = useMemo(() => {
    const ids = new Set();
    for (const item of checklist?.items || []) {
      for (const l of item.links || []) {
        if (l.inventory_item_id) ids.add(l.inventory_item_id);
      }
    }
    return ids;
  }, [checklist]);
  const [notes, setNotes] = useState(() => {
    const m = {};
    for (const n of checklist?.notes || []) m[n.category || ''] = n.note;
    return m;
  });
  const [localCats, setLocalCats] = useState([]);
  const [newCatInput, setNewCatInput] = useState(false);
  const [newCatZone, setNewCatZone] = useState(null);
  const [newCatValue, setNewCatValue] = useState('');
  const [localZones, setLocalZones] = useState([]);
  const [newZoneInput, setNewZoneInput] = useState(false);
  const [newZoneValue, setNewZoneValue] = useState('');
  const newCatRef = useRef(null);
  const newZoneRef = useRef(null);
  const [pendingAdd, setPendingAdd] = useState(null); // { zone, category } — auto-abre el input de alta
  const [renameTarget, setRenameTarget] = useState(null); // { kind, zone, category, isLocal }
  const [renameValue, setRenameValue] = useState('');
  const renameRef = useRef(null);

  useEffect(() => { if (newCatInput && newCatRef.current) newCatRef.current.focus(); }, [newCatInput, newCatZone]);
  useEffect(() => { if (newZoneInput && newZoneRef.current) newZoneRef.current.focus(); }, [newZoneInput]);
  useEffect(() => {
    if (renameTarget && renameRef.current) { renameRef.current.focus(); renameRef.current.select(); }
  }, [renameTarget]);

  const saveNote = async (category, note) => {
    if (!checklist) return;
    try {
      if (note) {
        await api.put(`/checklists/${checklist.id}/notes`, { category: category || null, note });
        setNotes(prev => ({ ...prev, [category]: note }));
      } else {
        await api.delete(`/checklists/${checklist.id}/notes`, { data: { category: category || null } });
        setNotes(prev => { const n = { ...prev }; delete n[category]; return n; });
      }
    } catch (e) { console.error(e); }
  };

  const addItem = async (zone, category, name) => {
    if (!checklist) return;
    try {
      const { data } = await api.post(`/checklists/${checklist.id}/items`, {
        zone: zone || null, category: category || null, name,
      });
      onItemAdded({ ...data, links: [] });
    } catch (e) { console.error(e); }
  };

  const createCategory = (zone) => {
    const name = newCatValue.trim();
    if (name) setLocalCats(prev => [...prev, { zone: zone || null, category: name }]);
    setNewCatValue('');
    setNewCatInput(false);
    setNewCatZone(null);
  };

  const createZone = () => {
    const name = newZoneValue.trim();
    if (name && !localZones.includes(name)) setLocalZones(prev => [...prev, name]);
    setNewZoneValue('');
    setNewZoneInput(false);
  };

  // ── Estado vacío: + Equipo (a GENERAL) / + Categoría / + Sector ──
  // byZone/zoneOrder se computan más abajo en este mismo render; los closures
  // de estos handlers solo se ejecutan en un click posterior, así que ya están
  // resueltos para entonces.
  const openGeneralAdd = () => {
    const hasGeneral = byZone.get('')?.has('');
    if (!hasGeneral) setLocalCats(prev => [...prev, { zone: null, category: null }]);
    setPendingAdd({ zone: '', category: '' });
  };
  const openRootCategoryAdd = () => {
    if (!zoneOrder.includes('')) setLocalZones(prev => [...prev, null]);
    setNewCatZone('');
    setNewCatInput(true);
    setNewCatValue('');
  };
  const openNewZoneEmpty = () => {
    setNewZoneInput(true);
    setNewZoneValue('');
  };

  // ── Renombrar / eliminar sector o categoría ──
  const startRename = (kind, zoneVal, categoryVal, isLocal, currentName) => {
    setRenameTarget({ kind, zone: zoneVal, category: categoryVal, isLocal });
    setRenameValue(currentName || '');
  };
  const cancelRename = () => { setRenameTarget(null); setRenameValue(''); };
  const commitRename = async () => {
    const target = renameTarget;
    if (!target) return;
    const name = renameValue.trim();
    setRenameTarget(null);
    setRenameValue('');
    if (!name) return;
    const { kind, zone: zoneVal, category: categoryVal, isLocal } = target;

    if (isLocal) {
      if (kind === 'zone') {
        setLocalZones(prev => prev.map(z => z === zoneVal ? name : z));
      } else {
        setLocalCats(prev => prev.map(c =>
          (c.zone || null) === (zoneVal || null) && (c.category || null) === (categoryVal || null)
            ? { ...c, category: name }
            : c
        ));
      }
      return;
    }
    if (!checklist) return;
    try {
      await api.put(`/checklists/${checklist.id}/sections/rename`, { kind, zone: zoneVal, category: categoryVal, newName: name });
      await onSectionsChanged?.();
    } catch (e) {
      console.error(e);
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const deleteSection = async (kind, zoneVal, categoryVal, isLocal, label) => {
    const consequence = kind === 'zone'
      ? 'Los equipos pasan a GENERAL / quedan sin sector; la nota de la sección se elimina.'
      : 'Los equipos quedan sin categoría; la nota de la sección se elimina.';
    if (!window.confirm(`¿Eliminar "${label}"?\n\n${consequence}`)) return;

    if (isLocal) {
      if (kind === 'zone') {
        setLocalZones(prev => prev.filter(z => z !== zoneVal));
      } else {
        setLocalCats(prev => prev.filter(c =>
          !((c.zone || null) === (zoneVal || null) && (c.category || null) === (categoryVal || null))
        ));
      }
      return;
    }
    if (!checklist) return;
    try {
      await api.delete(`/checklists/${checklist.id}/sections`, { data: { kind, zone: zoneVal, category: categoryVal } });
      await onSectionsChanged?.();
    } catch (e) {
      console.error(e);
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  // Build zone → category → items structure
  const byZone = new Map();
  const zoneOrder = [];
  for (const item of checklist?.items || []) {
    const z = item.zone || '';
    const c = item.category || '';
    if (!byZone.has(z)) { byZone.set(z, new Map()); zoneOrder.push(z); }
    const bycat = byZone.get(z);
    if (!bycat.has(c)) bycat.set(c, []);
    bycat.get(c).push(item);
  }
  for (const { zone, category } of localCats) {
    const z = zone || '';
    if (!byZone.has(z)) { byZone.set(z, new Map()); zoneOrder.push(z); }
    const bycat = byZone.get(z);
    const c = category || '';
    if (!bycat.has(c)) bycat.set(c, []);
  }
  for (const zone of localZones) {
    const z = zone || '';
    if (!byZone.has(z)) { byZone.set(z, new Map()); zoneOrder.push(z); }
  }

  // Progreso de conciliación: ítems con equipo asignado (links) sobre el total.
  // Solo lectura del estado existente — no cambia datos ni llamadas.
  const totalItems = (checklist?.items || []).length;
  const conciliados = (checklist?.items || []).filter(i => (i.links || []).length > 0).length;
  const progressPct = totalItems ? Math.round((conciliados / totalItems) * 100) : 0;
  const allConciliado = totalItems > 0 && conciliados === totalItems;
  // Equipos con conflicto de stock en este evento (pedido > disponible real):
  // mismos datos que pintan el ⚠ de cada chip, resumidos en el header.
  const conflictCount = useMemo(
    () => Object.values(conflictsData?.by_name || {})
      .filter(v => ((v.demand || 0) - (v.total || 0)) > 0).length,
    [conflictsData]
  );

  return (
    <div className={`ep-col ep-col--${event.status}`}>
      <div className="ep-col-header">
        <div className="ep-col-header-top">
          <div className="ep-event-name">{event.name}</div>
          <span className={`ep-badge ep-badge--${event.status}`}>{event.status}</span>
        </div>
        {matchInfo && matchInfo.count > 0 && (
          <div className="ep-match-count">{matchInfo.count} de {matchInfo.total} coinciden</div>
        )}
        {event.place && (
          <div className="ep-event-meta">{event.place}</div>
        )}
        {event.setup_start && (
          <div className="ep-event-setup">
            Armado: {formatArmado(event.setup_date, event.setup_start, event.event_date)}
          </div>
        )}
        {(event.start_time || event.end_time) && (
          <div className="ep-event-time">
            Evento: {event.start_time?.slice(0, 5)}{event.end_time && ` - ${event.end_time.slice(0, 5)}`}
          </div>
        )}
        {totalItems > 0 && (
          <div className="ep-progress">
            <div className="ep-progress-head">
              <span className="ep-progress-label">Progreso</span>
              <span className="ep-progress-right">
                {conflictCount > 0 && (
                  <span
                    className="ep-progress-conflicts"
                    title={`${conflictCount} equipo${conflictCount !== 1 ? 's' : ''} con conflicto de stock`}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    {conflictCount}
                  </span>
                )}
                <span className="ep-progress-count">{conciliados} / {totalItems} conciliado</span>
              </span>
            </div>
            <div className="ep-progress-track">
              <div
                className={`ep-progress-fill ep-progress-fill--${allConciliado ? 'done' : 'pending'}`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <div className={`ep-col-body${hayMasAbajo ? ' ep-col-body--mas' : ''}`} ref={bodyRef}>
        {canEdit && !inLinkMode && (
          <div className="ep-link-picker">
            <button
              className="ep-link-picker-btn"
              onClick={() => onEnterLinkMode?.(event)}
              disabled={!hasLinkCandidates}
              title={!hasLinkCandidates ? 'No hay eventos cerca para vincular' : 'Vincular equipos desde otro evento'}
            >
              <LinkGlyph /> Vincular desde otro evento
            </button>
          </div>
        )}
        {!checklist ? (
          <div className="ep-no-list">Sin lista de equipos</div>
        ) : zoneOrder.length === 0 ? (
          <div className="ep-empty-section">
            <div className="ep-empty-section-msg">Este evento todavía no tiene equipos</div>
            {canEdit && (
              <div className="ep-empty-section-actions">
                <button className="ep-empty-section-btn ep-empty-section-btn--primary" onClick={openGeneralAdd}>+ Equipo</button>
                <button className="ep-empty-section-btn" onClick={openRootCategoryAdd}>+ Categoría</button>
                <button className="ep-empty-section-btn" onClick={openNewZoneEmpty}>+ Sector</button>
              </div>
            )}
          </div>
        ) : (
          <>
            {zoneOrder.map(z => {
              const bycat = byZone.get(z);
              const catOrder = [...bycat.keys()];
              const zoneTotalItems = catOrder.reduce((n, c) => n + (bycat.get(c)?.length || 0), 0);
              const zoneIsLocal = zoneTotalItems === 0 && localZones.includes(z || null);
              const isRenamingZone = renameTarget?.kind === 'zone' && (renameTarget.zone || '') === z;
              return (
                <div key={z} className="ep-sector">
                  <div className="ep-sector-title">
                    {isRenamingZone ? (
                      <input
                        ref={renameRef}
                        className="ep-section-rename-input"
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
                          if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                        }}
                        onBlur={commitRename}
                      />
                    ) : (
                      <span>{z || 'GENERAL'}</span>
                    )}
                    {canEdit && !!z && !isRenamingZone && (
                      <div className="ep-section-actions">
                        <button
                          className="ep-section-action-btn"
                          title="Renombrar sector"
                          aria-label="Renombrar sector"
                          onClick={() => startRename('zone', z || null, null, zoneIsLocal, z)}
                        ><IconPencil /></button>
                        <button
                          className="ep-section-action-btn"
                          title="Eliminar sector"
                          aria-label="Eliminar sector"
                          onClick={() => deleteSection('zone', z || null, null, zoneIsLocal, z)}
                        ><IconTrash /></button>
                      </div>
                    )}
                  </div>
                  {catOrder.map(c => {
                    const catItems = bycat.get(c) || [];
                    const isGeneralSection = !z && !c;
                    const catIsLocal = catItems.length === 0 && localCats.some(lc =>
                      (lc.zone || '') === z && (lc.category || '') === c
                    );
                    const isRenamingCat = renameTarget?.kind === 'category'
                      && (renameTarget.zone || '') === z && (renameTarget.category || '') === c;
                    const catLabel = c || 'General';
                    return (
                      <CategorySection
                        key={c}
                        matchedIds={matchedIds}
                        zone={z}
                        cat={c}
                        items={catItems}
                        note={notes[c] || ''}
                        onSaveNote={saveNote}
                        onAddItem={(cat, name) => addItem(z, cat, name)}
                        onDeleteItem={onDeleteItem}
                        onLinksChange={onLinksChange}
                        activeItemId={activeItemId}
                        onActivate={onActivate}
                        onConsolidated={onConsolidated}
                        onArrowOut={onArrowOut}
                        conflictsData={conflictsData}
                        groupCtx={groupCtx}
                        onConflictEventClick={onConflictEventClick}
                        committedIds={committedIds}
                        eventId={event.id}
                        inLinkMode={inLinkMode}
                        onLinkLine={onLinkLine}
                        onUnlinkLine={onUnlinkLine}
                        canEdit={canEdit}
                        onLineDragStart={onLineDragStart}
                        pendingAdd={pendingAdd}
                        onConsumePendingAdd={() => setPendingAdd(null)}
                        isRenaming={isRenamingCat}
                        renameValue={renameValue}
                        onRenameChange={e => setRenameValue(e.target.value)}
                        onRenameKeyDown={e => {
                          if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
                          if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                        }}
                        onRenameBlur={commitRename}
                        renameInputRef={renameRef}
                        onStartRename={() => startRename('category', z || null, c || null, catIsLocal, c)}
                        onDeleteSection={() => deleteSection('category', z || null, c || null, catIsLocal, catLabel)}
                        showSectionActions={!isGeneralSection}
                      />
                    );
                  })}
                  {canEdit && <div className="ep-new-cat">
                    {newCatInput && newCatZone === z ? (
                      <input
                        ref={newCatRef}
                        className="ep-new-cat-input"
                        placeholder="Nombre de categoría…"
                        value={newCatValue}
                        onChange={e => setNewCatValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') { e.preventDefault(); createCategory(z); }
                          if (e.key === 'Escape') { setNewCatInput(false); setNewCatValue(''); setNewCatZone(null); }
                        }}
                        onBlur={() => createCategory(z)}
                      />
                    ) : (
                      <button className="ep-new-cat-btn" onClick={() => { setNewCatZone(z); setNewCatInput(true); setNewCatValue(''); }}>
                        + Nueva categoría
                      </button>
                    )}
                  </div>}
                </div>
              );
            })}
            {canEdit && <div className="ep-new-zone">
              {newZoneInput ? (
                <input
                  ref={newZoneRef}
                  className="ep-new-cat-input"
                  placeholder="Nombre del sector…"
                  value={newZoneValue}
                  onChange={e => setNewZoneValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); createZone(); }
                    if (e.key === 'Escape') { setNewZoneInput(false); setNewZoneValue(''); }
                  }}
                  onBlur={createZone}
                />
              ) : (
                <button className="ep-new-cat-btn" onClick={() => { setNewZoneInput(true); setNewZoneValue(''); }}>
                  + Sector
                </button>
              )}
            </div>}
          </>
        )}
        {/* Ultimo hijo a propósito: `sticky bottom` sólo empuja la caja hacia
            arriba, así que como primer hijo quedaba clavada arriba de todo y
            recortada por el borde superior del scroller. */}
        <span className="ep-scroll-more" aria-hidden="true">↓ más</span>
      </div>
    </div>
  );
}
